'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

type ThemeOption = { slug: string; name: string; isDefault?: boolean; isSystem?: boolean };

type Props = {
  themes: ThemeOption[];
  defaultTheme: string;
  isAdmin: boolean;
  isAuthenticated: boolean;
  userLabel?: string;
};

const STORAGE_KEY = 'bs_theme_preference';

function readCookie(key: string) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${key}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(key: string, value: string) {
  if (typeof document === 'undefined') return;
  document.cookie = `${key}=${encodeURIComponent(value)}; path=/; max-age=31536000; samesite=lax`;
}

function applyTheme(slug: string) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', slug);
  document.body?.setAttribute('data-theme', slug);
}

function readStoredPreference() {
  if (typeof window === 'undefined') return null;
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEY);
  } catch (error) {
    stored = null;
  }
  if (stored === null) {
    stored = readCookie(STORAGE_KEY);
  }
  return stored;
}

function resolveTheme(preference: string | null, fallback: string) {
  if (typeof window === 'undefined') return fallback;
  if (preference === 'auto' || preference === null) {
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    return prefersDark ? 'dark' : 'light';
  }
  return preference || fallback;
}

export default function ThemeSwitcher({ themes, defaultTheme, isAdmin, isAuthenticated, userLabel }: Props) {
  const pathname = usePathname() || '';
  const isLoginScreen = pathname === '/login' || pathname === '/admin/login';
  const [preference, setPreference] = useState<string>('auto');
  const [open, setOpen] = useState(false);
  const [themesOpen, setThemesOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const stored = readStoredPreference();
    const resolved = resolveTheme(stored, defaultTheme);
    setPreference(stored ?? 'auto');
    applyTheme(resolved);
  }, [defaultTheme]);

  useEffect(() => {
    function handleDocClick(event: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
        setThemesOpen(false);
      }
    }

    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        setThemesOpen(false);
      }
    }

    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  function handleChange(next: string) {
    setPreference(next);
    let resolved = next;
    try {
      if (next === 'auto') {
        localStorage.setItem(STORAGE_KEY, 'auto');
        writeCookie(STORAGE_KEY, 'auto');
        resolved = resolveTheme('auto', defaultTheme);
      } else {
        localStorage.setItem(STORAGE_KEY, next);
        writeCookie(STORAGE_KEY, next);
      }
    } catch (error) {
      writeCookie(STORAGE_KEY, next);
      if (next === 'auto') {
        resolved = resolveTheme('auto', defaultTheme);
      }
    }
    applyTheme(resolved);
    setOpen(false);
    setThemesOpen(false);
  }

  function closeMenus() {
    setOpen(false);
    setThemesOpen(false);
  }

  async function handleLogout() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await fetch('/api/auth/logout', { method: 'POST' });
    } finally {
      window.location.href = '/login';
    }
  }

  return (
    <div className="theme-switcher" ref={wrapperRef}>
      <button
        className="theme-switcher-trigger"
        type="button"
        title="Settings"
        aria-label="Settings"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          setOpen(prev => {
            const next = !prev;
            if (!next) setThemesOpen(false);
            return next;
          });
        }}
      >
        <span className="theme-switcher-icon" aria-hidden="true" />
      </button>
      {open ? (
        <div className="theme-switcher-menu" role="menu">
          <div className={`theme-switcher-submenu-wrap${themesOpen ? ' is-open' : ''}`}>
            <button
              className={`theme-switcher-item theme-switcher-submenu-trigger${themesOpen ? ' is-open' : ''}`}
              type="button"
              role="menuitem"
              aria-expanded={themesOpen}
              onClick={() => setThemesOpen(prev => !prev)}
            >
              <span>Color Themes</span>
              <span className="theme-switcher-submenu-caret" aria-hidden>
                ▾
              </span>
            </button>
            {themesOpen ? (
              <div className="theme-switcher-submenu" role="menu">
                <button
                  className={`theme-switcher-item${preference === 'auto' ? ' is-active' : ''}`}
                  type="button"
                  role="menuitem"
                  onClick={() => handleChange('auto')}
                >
                  Auto
                </button>
                {themes.map(theme => (
                  <button
                    key={theme.slug}
                    className={`theme-switcher-item${preference === theme.slug ? ' is-active' : ''}`}
                    type="button"
                    role="menuitem"
                    onClick={() => handleChange(theme.slug)}
                  >
                    {theme.name}
                    {theme.isDefault ? ' (Default)' : ''}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          {!isLoginScreen ? (
            <Link className="theme-switcher-link" href="/sitemap" role="menuitem" onClick={closeMenus}>
              Sitemap
            </Link>
          ) : null}
          {isAdmin ? (
            <Link className="theme-switcher-link" href="/admin" role="menuitem" onClick={closeMenus}>
              Admin Settings
            </Link>
          ) : null}
          {isAuthenticated ? (
            <>
              <button className="theme-switcher-item" type="button" role="menuitem" onClick={handleLogout} disabled={signingOut}>
                {signingOut ? 'Signing out…' : 'Logout'}
              </button>
              <div className="theme-switcher-label">{userLabel || 'Signed in'}</div>
            </>
          ) : (
            <Link className="theme-switcher-link" href="/login" role="menuitem" onClick={closeMenus}>
              Login
            </Link>
          )}
        </div>
      ) : null}
    </div>
  );
}
